import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Trans } from '@lingui/react';
import { Radio } from '@startuptools/ui/Radio';
import { RadioGroup } from '@startuptools/ui/RadioGroup';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { captureException } from '@sentry/react';
import { toast } from '@startuptools/ui/toast/use-toast';
import { ErrorDisplay } from '../../../../components/error-display/ErrorDisplay';
import { GqlShareIssue } from '../../../../graphql/base-types.graphql';
import { useStepper } from '../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../components/steppers/common/StepperStepContentContext';
import { ShareIssueDocument } from '../../ShareIssue.graphql';
import { useSiSetLanguageMutation } from '../../../../graphql/react-operations';

type FormValues = {
  secondaryLang: 'true' | 'false';
};

export const SiStepLanguages = ({
  shareIssue,
}: {
  shareIssue: Pick<GqlShareIssue, 'id' | 'companyId' | 'secondaryLang'>;
}) => {
  // eslint-disable-next-line sut/loading-mutation
  const [doSetLanguage] = useSiSetLanguageMutation({ refetchQueries: [ShareIssueDocument] });
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      secondaryLang: `${shareIssue.secondaryLang}`,
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async values => {
    try {
      await doSetLanguage({
        variables: {
          companyId: shareIssue.companyId,
          input: { id: shareIssue.id, primaryLang: true, secondaryLang: values.secondaryLang === 'true' },
        },
      });
    } catch (e) {
      captureException(e);
      toast({
        severity: 'danger',
        title: <Trans id="Could not save share price" />,
        description: <Trans id="Could not save language, please try again and contact support if problem persists" />,
      });
    }
    return stepNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="Select Langage" />
        </Typography>

        <Controller
          control={control}
          name="secondaryLang"
          render={({ field }) => (
            <RadioGroup {...field}>
              <Radio value={false} label={<Trans id="Swedish Only" />} />
              <Radio value label={<Trans id="Swedish and English" />} />
            </RadioGroup>
          )}
        />

        {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

        <Box>
          <Button type="submit">
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
